<div
  class="esn-table"
  role="table"
  [class.esn-table--padded]="cellPadding()"
  [style.--esn-table-inset.px]="inset()"
  [attr.aria-label]="ariaLabel()"
  [attr.aria-busy]="loading() || null"
>
  <div role="rowgroup">
    <div class="esn-table__header" role="row">
      @if (selectable()) {
        <div class="esn-table__gutter" role="columnheader">
          <button
            type="button"
            class="esn-table__check"
            role="checkbox"
            [attr.aria-checked]="allSelected()"
            [attr.aria-label]="selectAllLabel()"
            [disabled]="rows().length === 0"
            (click)="toggleAll()"
          >
            <esn-icon [name]="allSelected() ? 'checkbox-checked' : 'checkbox'" [size]="18" />
          </button>
        </div>
      }
      @if (expandable()) {
        <div class="esn-table__gutter" role="columnheader"></div>
      }
      @for (column of columns(); track column.key; let i = $index) {
        <div
          class="esn-table__cell esn-table__cell--head"
          [class]="'esn-table__cell--' + (column.align ?? 'start')"
          role="columnheader"
          [style]="styles()[i]"
          [attr.aria-sort]="ariaSort(column)"
        >
          @if (column.sortable) {
            <button type="button" class="esn-table__sort" (click)="toggleSort(column)">
              {{ column.header }}
              <esn-icon
                name="chevron-down"
                [size]="12"
                class="esn-table__sort-icon"
                [class.esn-table__sort-icon--active]="sort()?.key === column.key"
                [class.esn-table__sort-icon--asc]="sort()?.key === column.key && sort()?.direction === 'asc'"
              />
            </button>
          } @else {
            <span class="esn-table__label">{{ column.header }}</span>
          }
        </div>
      }
    </div>
  </div>

  <div class="esn-table__body" role="rowgroup">
    @if (loading()) {
      @for (s of skeletons(); track s) {
        <div class="esn-table__row esn-table__row--loading" role="row" [style.height.px]="rowHeight()">
          @if (selectable()) {
            <div class="esn-table__gutter" role="cell"></div>
          }
          @if (expandable()) {
            <div class="esn-table__gutter" role="cell"></div>
          }
          @for (column of columns(); track column.key; let i = $index) {
            <div class="esn-table__cell" [class]="'esn-table__cell--' + (column.align ?? 'start')" role="cell" [style]="styles()[i]">
              @if (column.header) {
                <span class="esn-table__skeleton"></span>
              }
            </div>
          }
        </div>
      }
    } @else if (viewRows().length === 0) {
      <div class="esn-table__row esn-table__row--empty" role="row">
        <div class="esn-table__empty" role="cell">
          @if (emptyTemplate(); as empty) {
            <ng-container [ngTemplateOutlet]="empty.template" />
          } @else {
            {{ emptyText() }}
          }
        </div>
      </div>
    } @else {
      @for (row of viewRows(); track keyOf(row); let index = $index) {
        <div
          class="esn-table__row"
          role="row"
          [class.esn-table__row--clickable]="clickableRows()"
          [class.esn-table__row--selected]="selectedSet().has(keyOf(row))"
          [style.height.px]="rowHeight()"
          [attr.tabindex]="clickableRows() ? 0 : null"
          [attr.aria-selected]="selectable() ? selectedSet().has(keyOf(row)) : null"
          (click)="activate(row, $event)"
          (keydown.enter)="activate(row, $event)"
          (keydown.space)="activate(row, $event)"
        >
          @if (selectable()) {
            <div class="esn-table__gutter" role="cell">
              <button
                type="button"
                class="esn-table__check"
                role="checkbox"
                [attr.aria-checked]="selectedSet().has(keyOf(row))"
                [attr.aria-label]="selectRowLabel()"
                (click)="toggleRow(row, $event)"
                (keydown.enter)="$event.stopPropagation()"
                (keydown.space)="$event.stopPropagation()"
              >
                <esn-icon [name]="selectedSet().has(keyOf(row)) ? 'checkbox-checked' : 'checkbox'" [size]="18" />
              </button>
            </div>
          }
          @if (expandable()) {
            <div class="esn-table__gutter" role="cell">
              <button
                type="button"
                class="esn-table__expand"
                [class.esn-table__expand--open]="expandedSet().has(keyOf(row))"
                [attr.aria-expanded]="expandedSet().has(keyOf(row))"
                [attr.aria-label]="expandLabel()"
                (click)="toggleExpanded(row, $event)"
                (keydown.enter)="$event.stopPropagation()"
                (keydown.space)="$event.stopPropagation()"
              >
                <esn-icon name="chevron-expand" [size]="20" />
              </button>
            </div>
          }
          @for (column of columns(); track column.key; let i = $index) {
            <div class="esn-table__cell" [class]="'esn-table__cell--' + (column.align ?? 'start')" role="cell" [style]="styles()[i]">
              @if (templateByKey().get(column.key); as template) {
                <ng-container
                  [ngTemplateOutlet]="template"
                  [ngTemplateOutletContext]="{ $implicit: row, value: valueOf(row, column.key), column, index }"
                />
              } @else {
                <span class="esn-table__text">{{ valueOf(row, column.key) }}</span>
              }
            </div>
          }
        </div>
        @if (expandable() && expandedSet().has(keyOf(row)) && detailTemplate(); as detail) {
          <div class="esn-table__detail" role="row">
            <div role="cell" class="esn-table__detail-cell">
              <ng-container [ngTemplateOutlet]="detail.template" [ngTemplateOutletContext]="{ $implicit: row, index }" />
            </div>
          </div>
        }
      }
    }
  </div>
</div>
